import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from "@angular/core";
import { DataAccessService } from "@kells/apis/data-access";
import {
  PLANS_PRIORITY,
  Product,
  SubscriptionPlan,
} from "@kells/interfaces/patient";
import { BehaviorSubject, combineLatest, Observable, of } from "rxjs";
import { SubSink } from "subsink";
import { catchError, map, take } from "rxjs/operators";
import { MatSlideToggleChange } from "@angular/material/slide-toggle";
import { observeProperty } from "@kells/utils/angular";
import { AnalyticsService } from "@kells/apis/analytics";
import { STRIPE_BILLING_LOGIN_URL } from "apps/patient-reporting/src/app/shared/constants";
import {
  SelectPlanClick,
  UpgradePlanClick,
} from "apps/patient-reporting/src/app/shared/models/analytics/home-events";
import { TermsModalComponent } from "../../terms-modal/terms-modal.component";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { UpgradePlanService } from "../../../services";

export enum UpgradePlanListMode {
  Plans = "plans",
  Address = "address",
}

export interface PlanFeature {
  label: string;
  value: string;
  bold?: boolean;
  high?: boolean;
}

export interface Plan {
  type: SubscriptionPlan;
  isCurrent?: boolean;
  isAnnual?: boolean;
  isCurrentCycle?: boolean;
  actionText: string;
  product?: Product;
  requiresTerms?: boolean;
  features: Record<string, string | boolean>;
}

@Component({
  selector: "koa-upgrade-plan-list",
  templateUrl: "./upgrade-plan-list.component.html",
  styleUrls: ["./upgrade-plan-list.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpgradePlanListComponent implements OnDestroy {
  @Input() availableUpgrades: SubscriptionPlan[] = [
    SubscriptionPlan.Basic,
    SubscriptionPlan.Premium,
    SubscriptionPlan.Complete,
  ];

  @Input() selectedPlan: SubscriptionPlan | null = null;

  @Output() modeChange = new EventEmitter<UpgradePlanListMode>();

  public readonly UpgradePlanListMode = UpgradePlanListMode;

  public readonly mode$ = new BehaviorSubject<UpgradePlanListMode>(
    UpgradePlanListMode.Plans
  );

  termsAccepted = false;

  currentPlan: SubscriptionPlan;

  public readonly selectedPatient$ = this.dataService.selectedPatient$;

  public readonly currentPlan$ = this.dataService
    .selectedPatientSubscriptionPlan$;

  public readonly currentPlanCycle$ = this.dataService
    .selectedPatientPlanCycle$;

  public readonly isSelectingPlan$ = new BehaviorSubject<SubscriptionPlan | null>(
    null
  );

  public readonly selectedPlan$ = new BehaviorSubject<SubscriptionPlan | null>(
    null
  );

  public readonly SubscriptionPlan = SubscriptionPlan;

  public readonly stipeProducts$ = this.dataService.products$;

  public readonly isProviderNetworkAvailable$ = this.dataService.hiddenData$.pipe(
    map((hiddenData) => !hiddenData.providerNetwork)
  );

  public readonly isAnnualUpgradePlanAvailable$ = this.dataService.hiddenData$.pipe(
    map((hiddenData) => !hiddenData.upgradePlan.annual)
  );

  public readonly isMonthlyUpgradePlanAvailable$ = this.dataService.hiddenData$.pipe(
    map((hiddenData) => !hiddenData.upgradePlan.monthly)
  );

  public readonly isFrequencyToggleAvailable$ = combineLatest([
    this.isAnnualUpgradePlanAvailable$,
    this.isMonthlyUpgradePlanAvailable$,
  ]).pipe(
    map(
      ([isAnnualUpgradePlanAvailable, isMonthlyUpgradePlanAvailable]) =>
        isAnnualUpgradePlanAvailable && isMonthlyUpgradePlanAvailable
    )
  );

  public readonly isAnnual$ = new BehaviorSubject<boolean>(false);

  public onIsAnnualChange(event: MatSlideToggleChange) {
    this.isAnnual$.next(event.checked);
  }

  public readonly features$: Observable<
    PlanFeature[]
  > = this.isProviderNetworkAvailable$.pipe(
    map((isProviderNetworkAvailable) => [
      {
        label: "Dental Advisor",
        value: "dental_advisor",
      },
      {
        label: "AI Dental Scan",
        value: "ai_dental_scan",
      },
      {
        label: "Digital Oral Health Report",
        value: "digital_oral_health_report",
      },
      {
        label: "Product Recommendation",
        value: "product_recommendation",
      },
      {
        label: "Virtual Second Opinion",
        value: "virtual_second_opinion",
      },
      {
        label: "Benefits Navigation",
        value: "benefits_navigation",
      },
      {
        label: "Concierge Care Coordination",
        value: "concierge_care_coordination",
      },
      {
        label: "Dentist Consultation",
        value: "dentist_consultation",
      },
      ...(isProviderNetworkAvailable
        ? [
            {
              label: "Access to KELLS Provider Network",
              value: "access_to_kells_provider_network",
              bold: true,
              high: true,
            },
          ]
        : []),
    ])
  );

  public readonly plans$ = combineLatest([
    this.currentPlan$,
    this.stipeProducts$,
    this.isProviderNetworkAvailable$,
    this.isAnnual$,
    this.currentPlanCycle$,
  ]).pipe(
    map(
      ([
        currentPlan,
        products,
        isProviderNetworkAvailable,
        isAnnual,
        currentPlanCycle,
      ]): Plan[] => {
        const completePlanProductMonthly = this.upgradePlanService.getCompletePlanProduct(
          products,
          "month"
        );
        const completePlanProductAnnual = this.upgradePlanService.getCompletePlanProduct(
          products,
          "year"
        );
        const premiumPlanProductMonthly = this.upgradePlanService.getPremiumPlanProduct(
          products,
          "month"
        );
        const premiumPlanProductAnnual = this.upgradePlanService.getPremiumPlanProduct(
          products,
          "year"
        );
        const currentPremiumPlanProduct = isAnnual
          ? premiumPlanProductAnnual
          : premiumPlanProductMonthly;
        const currentCompletePlanProduct = isAnnual
          ? completePlanProductAnnual
          : completePlanProductMonthly;
        const isCurrentCycle =
          (currentPlanCycle === "month" && !isAnnual) ||
          (currentPlanCycle === "year" && isAnnual);

        return [
          {
            type: SubscriptionPlan.Basic,
            isCurrent: currentPlan === SubscriptionPlan.Basic,
            isAnnual: isAnnual,
            isCurrentCycle: false,
            actionText: this.getActionText(
              SubscriptionPlan.Basic,
              currentPlan,
              isAnnual,
              0
            ),
            features: {
              dental_advisor: "Max. 5 Questions",
              ai_dental_scan: "Once",
              digital_oral_health_report: "Online view only",
              product_recommendation: "Recommendation only",
              virtual_second_opinion: false,
              benefits_navigation: false,
              concierge_care_coordination: false,
              dentist_consultation: false,
              ...(isProviderNetworkAvailable
                ? {
                    access_to_kells_provider_network: false,
                  }
                : {}),
            },
          },
          {
            type: SubscriptionPlan.Premium,
            actionText: this.getActionText(
              SubscriptionPlan.Premium,
              currentPlan,
              isAnnual,
              currentPremiumPlanProduct?.price_amount
            ),
            isCurrent: currentPlan === SubscriptionPlan.Premium,
            isAnnual: isAnnual,
            isCurrentCycle: isCurrentCycle,
            product: currentPremiumPlanProduct,
            features: {
              dental_advisor: "Unlimited",
              ai_dental_scan: "Unlimited",
              digital_oral_health_report: "Offline export",
              product_recommendation: "Exclusive discounts",
              virtual_second_opinion: true,
              benefits_navigation: true,
              concierge_care_coordination: true,
              dentist_consultation: true,
              ...(isProviderNetworkAvailable
                ? {
                    access_to_kells_provider_network: false,
                  }
                : {}),
            },
          },
          ...(isProviderNetworkAvailable
            ? [
                {
                  type: SubscriptionPlan.Complete,
                  actionText: this.getActionText(
                    SubscriptionPlan.Complete,
                    currentPlan,
                    isAnnual,
                    currentCompletePlanProduct?.price_amount
                  ),
                  isCurrent: currentPlan === SubscriptionPlan.Complete,
                  isAnnual: isAnnual,
                  isCurrentCycle: isCurrentCycle,
                  product: currentCompletePlanProduct,
                  requiresTerms: true,
                  features: {
                    dental_advisor: "Unlimited",
                    ai_dental_scan: "Unlimited",
                    digital_oral_health_report: "Offline export",
                    product_recommendation: "Exclusive discounts",
                    virtual_second_opinion: true,
                    benefits_navigation: true,
                    concierge_care_coordination: true,
                    dentist_consultation: true,
                    ...(isProviderNetworkAvailable
                      ? {
                          access_to_kells_provider_network: true,
                        }
                      : {}),
                  },
                },
              ]
            : []),
        ];
      }
    )
  );

  private _subs = new SubSink();

  constructor(
    private readonly dataService: DataAccessService,
    private readonly analyticsService: AnalyticsService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly upgradePlanService: UpgradePlanService
  ) {
    this._subs.sink = combineLatest([
      observeProperty(this, "selectedPlan"),
      this.currentPlan$,
    ]).subscribe(([selectedPlan, currentPlan]) => {
      this.currentPlan = currentPlan;
      this.selectedPlan$.next(selectedPlan ?? currentPlan);
    });

    this._subs.sink = combineLatest([
      this.isAnnualUpgradePlanAvailable$,
      this.isMonthlyUpgradePlanAvailable$,
    ]).subscribe(
      ([isAnnualUpgradePlanAvailable, isMonthlyUpgradePlanAvailable]) => {
        this.isAnnual$.next(
          isAnnualUpgradePlanAvailable && !isMonthlyUpgradePlanAvailable
        );
      }
    );
  }

  ngOnDestroy() {
    this._subs.unsubscribe();
  }

  public getPlanFeatures(plan: Plan) {
    return Object.keys(plan.features);
  }

  public isBoolean(value: string | boolean) {
    return typeof value === "boolean";
  }

  public isAvailableUpgrade(plan: Plan) {
    return this.availableUpgrades.includes(plan.type);
  }

  public getActionText(
    plan: SubscriptionPlan,
    currentPlan: SubscriptionPlan,
    isAnnual: boolean,
    price?: number
  ) {
    const currentPlanPriority = PLANS_PRIORITY[currentPlan];
    const planPriority = PLANS_PRIORITY[plan];
    let prefix = "";

    if (planPriority > currentPlanPriority) {
      prefix = "Upgrade";
    } else if (planPriority < currentPlanPriority) {
      prefix = "Downgrade";
    }

    if (price && price > 0) {
      return `${prefix} $${price} / ${isAnnual ? "Year" : "Month"}`;
    }

    return `${prefix} Free`;
  }

  public isActionDisabled(plan: Plan) {
    const isDisabled =
      (plan.requiresTerms && !this.termsAccepted) ||
      this.isSelectingPlan$.value === plan.type;

    if (plan.isCurrent) {
      return isDisabled || plan.isCurrentCycle;
    }

    return isDisabled;
  }

  public selectPlan(plan: Plan) {
    this.analyticsService.record(
      UpgradePlanClick({
        plan: plan.type,
      })
    );

    if (plan.type === SubscriptionPlan.Complete) {
      this.setMode(UpgradePlanListMode.Address);
      this.modeChange.emit(UpgradePlanListMode.Address);
      return;
    }

    this.upgradePlan(plan);
  }

  public upgradePlan(plan: Plan) {
    this.isSelectingPlan$.next(plan.type);

    const currentPlanPriority = PLANS_PRIORITY[this.currentPlan];
    const selectedPlanPriority = PLANS_PRIORITY[plan.type];

    if (
      currentPlanPriority > selectedPlanPriority ||
      !plan.product ||
      (plan.isCurrent && !plan.isAnnual)
    ) {
      return window.open(STRIPE_BILLING_LOGIN_URL, "_self");
    }

    this._subs.sink = this.dataService
      .getUpgradePlanPaymentLink(plan.product, plan.type)
      .pipe(
        take(1),
        catchError((err) => {
          this.isSelectingPlan$.next(null);
          // Assuming MatSnackBar or similar is available or injecting it
          // Since MatDialog is injected, let's use it or just alert for now if no snackbar
          // actually, let's inject MatSnackBar in constructor first.
          // Wait, I need to check if MatSnackBar is available in imports.
          // It's not. I will add it.
          return of(null);
        })
      )
      .subscribe((checkoutUrl) => {
        if (!checkoutUrl) {
          this.snackBar.open(
            "There was an error creating the checkout session. Please ensure your address is complete.",
            "Close",
            { duration: 5000 }
          );
          return;
        }
        // Navigate to checkout - res is already the URL string
        if (typeof checkoutUrl === "string" && checkoutUrl.length > 0) {
          window.location.href = checkoutUrl;
        } else {
          this.isSelectingPlan$.next(null);
          this.snackBar.open("Failed to get checkout URL", "Close", {
            duration: 5000,
          });
        }
      });
  }

  public async upgradeToCompletePlan() {
    const plans = await this.plans$.pipe(take(1)).toPromise();
    const completedPlan = plans.find(
      (plan) => plan.type === SubscriptionPlan.Complete
    );

    if (completedPlan) {
      this.upgradePlan(completedPlan);
    }
  }

  public selectSelectPlan(plan: Plan) {
    if (this.isAvailableUpgrade(plan)) {
      this.analyticsService.record(
        SelectPlanClick({
          plan: plan.type,
        })
      );

      this.selectedPlan$.next(plan.type);
    }
  }

  public openTermsModal(event: Event) {
    event.preventDefault();

    this.dialog.open(TermsModalComponent, {
      autoFocus: false,
      panelClass: ["reports-modal"],
      width: "1250px",
      maxWidth: "100%",
    });
  }

  public setMode(mode: UpgradePlanListMode) {
    this.mode$.next(mode);
    this.modeChange.emit(mode);
  }
}
